import { memo } from "react";
import { ReactSVG } from "react-svg";
import { classNames, useTranslate } from "helpers";
import { MONTH_SHORT_NAMES, VIEW_OPTIONS } from "./consts";
import { CalendarToolbarProps } from "./types";

const navButtonClassName =
  "w-9 h-9 flex items-center justify-center text-gray-600 hover:bg-slate-100 focus:outline-none";

/**
 * `ReactSVG` stavlja `className` na omotač, a ne na sam `<svg>` — bez `[&_svg]:w-full`
 * ikona ostane bez veličine i gumb izgleda prazno. Isti zapis koristi i paginacija tablice.
 */
const navIconClassName =
  "flex items-center justify-center w-5 h-5 [&_svg]:w-full [&_svg]:h-full";

const CalendarToolbar: React.FC<CalendarToolbarProps> = (props) => {
  const {
    view,
    title,
    subtitle,
    onPrev,
    onNext,
    onToday,
    onViewChange,
  } = props;

  const { t } = useTranslate();
  const today = new Date();

  return (
    <div className="flex flex-wrap items-center gap-3 px-4 py-3 border-b border-slate-200">
      <div className="flex min-w-0 items-center gap-3">
        {/* Oznaka današnjeg datuma — stalni orijentir dok se listaju mjeseci. */}
        <div className="w-12 shrink-0 rounded-sm border border-slate-200 text-center py-1">
          <div className="text-[10px] font-semibold text-gray-400 leading-tight">
            {MONTH_SHORT_NAMES[today.getMonth()]}
          </div>
          <div className="text-lg font-semibold text-gray-800 leading-tight">
            {today.getDate()}
          </div>
        </div>
        <div className="min-w-0">
          <h2 className="text-lg font-semibold text-gray-800 leading-tight truncate">
            {title}
          </h2>
          <p className="text-xs text-gray-500 truncate">{subtitle}</p>
        </div>
      </div>

      {/* `ml-auto` umjesto `justify-between`: uz `flex-wrap` na vanjskom spremniku cijela
          grupa se prelomi u novi redak kad nema mjesta, ali ostaje u jednom retku. */}
      <div className="ml-auto flex shrink-0 items-center gap-3">
        <div className="inline-flex shrink-0 items-center rounded-sm border border-slate-300 divide-x divide-slate-300 overflow-hidden">
          <button
            type="button"
            onClick={onPrev}
            title={t("admin.notification.calendar.prev")}
            className={navButtonClassName}
          >
            <ReactSVG src="/media/icons/arrow-left.svg" className={navIconClassName} />
          </button>
          <button
            type="button"
            onClick={onToday}
            className={classNames(navButtonClassName, "w-auto px-4 text-sm font-medium")}
          >
            {t("admin.notification.calendar.today")}
          </button>
          <button
            type="button"
            onClick={onNext}
            title={t("admin.notification.calendar.next")}
            className={navButtonClassName}
          >
            <ReactSVG src="/media/icons/arrow-right.svg" className={navIconClassName} />
          </button>
        </div>

        <div className="inline-flex shrink-0 items-center gap-1 rounded-md bg-slate-100 p-1">
          {VIEW_OPTIONS.map((option) => {
            const handleViewClick = () => onViewChange(option.value);

            return (
              <button
                key={option.value}
                type="button"
                onClick={handleViewClick}
                className={classNames(
                  "px-4 py-1.5 text-sm font-medium rounded-sm transition-colors duration-200 focus:outline-none",
                  view === option.value
                    ? "bg-white text-sky-600 shadow-sm"
                    : "text-gray-500 hover:text-sky-600"
                )}
              >
                {option.label}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default memo(CalendarToolbar);
